/* eslint-env browser */
import { memo, useEffect } from 'react';
import { Global } from '@emotion/react';
import { css } from '@emotion/css';
import Head from 'next/head';
import Script from 'next/script';

import type { TemplateProps } from './BaseTemplate';

declare global {
  interface Window {
    themeOverride: Record<string, unknown>;
  }
}

function isIE(): number | false {
  if (typeof navigator === 'undefined') return false;

  const myNav = navigator.userAgent.toLowerCase();

  const list = myNav.split('msie');
  const firstItem = list[1];

  if (myNav.indexOf('msie') !== -1 && typeof firstItem !== 'undefined') {
    return parseInt(firstItem, 10);
  }

  return false;
}

function RsvpTemplate({ themeOverride }: TemplateProps) {
  useEffect((): void => {
    const tag = document.createElement('script');

    tag.async = false;

    tag.src = process.env['RSVP_BUNDLE'] || '';

    const body: HTMLBodyElement | undefined =
      document.getElementsByTagName('body')[0];

    if (typeof body !== 'undefined') {
      body.appendChild(tag);
    }

    if (themeOverride) {
      try {
        window.themeOverride = JSON.parse(themeOverride);
      } catch (err) {
        console.error(err);
      }
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const unsupportedBrowser = (): boolean | 0 => {
    // @ts-expect-error these errors are not important
    return isIE() && isIE() < 9;
  };

  // This is a hack to remove the global snackbar that shows Xs
  const removeXsOverride = `#Union__global-snackbar-container { display: none;}`;

  return (
    <>
      <Head>
        <link rel="stylesheet" href={process.env['RSVP_CSS_BUNDLE']} />

        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,shrink-to-fit=no"
        />
        <meta name="robots" content="noindex, nofollow" />
        <style>{removeXsOverride}</style>
        <Global
          styles={css`
            .error-message {
              background-color: rgba(245, 166, 35, 0.2);
              margin-bottom: 15px;
              text-align: left;
              font-size: 14px;
              padding: 13px 20px;
              border-left: 5px solid rgb(245, 166, 35);
            }

            #Union__global-snackbar-container {
              display: none;
            }
          `}
        />
      </Head>

      <Script src={process.env['RSVP_NEW_RELIC']} />

      <noscript>You need to enable JavaScript to run this app.</noscript>

      {unsupportedBrowser() ? (
        <div id="browser-not-supported" className="error-message">
          Oops! We’re having trouble with your browser. Please try again on
          another browser or device.
        </div>
      ) : null}

      <div id="root" />
    </>
  );
}

export default memo(RsvpTemplate);
